import { useState } from 'react'
import type { Position } from '../lib/supabase'

interface Props {
  positions: Position[]
  onJournalClick?: (ticker: string) => void
}

type SortKey = 'ticker' | 'market_value' | 'unrealized_pnl' | 'position_pct' | 'sleeve' | 'pct_to_stop'
type SortDir = 'asc' | 'desc'

function computePctToStop(p: { current_price: number; stop_price: number | null }): number | null {
  if (p.stop_price == null || p.current_price === 0) return null
  return ((p.current_price - p.stop_price) / p.current_price) * 100
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

function formatPercent(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
}

export function PositionsTable({ positions, onJournalClick }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('market_value')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [filterSleeve, setFilterSleeve] = useState<string | null>(null)

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const filteredPositions = filterSleeve
    ? positions.filter(p => p.sleeve === filterSleeve)
    : positions

  const sortedPositions = [...filteredPositions].sort((a, b) => {
    if (sortKey === 'pct_to_stop') {
      const aVal = computePctToStop(a)
      const bVal = computePctToStop(b)
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal
    }

    const aRaw = a[sortKey as keyof typeof a]
    const bRaw = b[sortKey as keyof typeof b]

    if (typeof aRaw === 'string' && typeof bRaw === 'string') {
      return sortDir === 'asc'
        ? aRaw.toLowerCase().localeCompare(bRaw.toLowerCase())
        : bRaw.toLowerCase().localeCompare(aRaw.toLowerCase())
    }

    const aNum = aRaw as number
    const bNum = bRaw as number
    if (aNum < bNum) return sortDir === 'asc' ? -1 : 1
    if (aNum > bNum) return sortDir === 'asc' ? 1 : -1
    return 0
  })

  const sleeveTotals = positions.reduce((acc, p) => {
    acc[p.sleeve] = (acc[p.sleeve] || 0) + p.market_value
    acc.total = (acc.total || 0) + p.market_value
    return acc
  }, {} as Record<string, number>)

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Positions ({positions.length})</h2>
        <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
          <button
            className={`card-badge ${!filterSleeve ? 'active' : ''}`}
            onClick={() => setFilterSleeve(null)}
            style={{
              cursor: 'pointer',
              border: 'none',
              background: !filterSleeve ? 'var(--accent-cyan-glow)' : 'var(--bg-tertiary)',
              color: !filterSleeve ? 'var(--accent-cyan)' : 'var(--text-muted)',
            }}
          >
            ALL
          </button>
          {['etf', 'stock', 'ipo', 'commod', 'crypto'].map(sleeve => (
            <button
              key={sleeve}
              className={`card-badge ${filterSleeve === sleeve ? 'active' : ''}`}
              onClick={() => setFilterSleeve(filterSleeve === sleeve ? null : sleeve)}
              style={{
                cursor: 'pointer',
                border: 'none',
                background: filterSleeve === sleeve
                  ? `var(--sleeve-${sleeve})20`
                  : 'var(--bg-tertiary)',
                color: filterSleeve === sleeve
                  ? `var(--sleeve-${sleeve})`
                  : 'var(--text-muted)',
              }}
            >
              {sleeve.toUpperCase()} ({positions.filter(p => p.sleeve === sleeve).length})
            </button>
          ))}
        </div>
      </div>

      {sortedPositions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📈</div>
          <div className="empty-state-text">No positions to display</div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="positions-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('ticker')} style={{ cursor: 'pointer' }}>
                  Ticker {sortKey === 'ticker' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('sleeve')} style={{ cursor: 'pointer' }}>
                  Sleeve {sortKey === 'sleeve' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th className="numeric">Qty</th>
                <th className="numeric">Avg Cost</th>
                <th className="numeric">Price</th>
                <th className="numeric" onClick={() => handleSort('unrealized_pnl')} style={{ cursor: 'pointer' }}>
                  P&L {sortKey === 'unrealized_pnl' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th className="numeric" onClick={() => handleSort('market_value')} style={{ cursor: 'pointer' }}>
                  Value {sortKey === 'market_value' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th className="numeric" onClick={() => handleSort('position_pct')} style={{ cursor: 'pointer' }}>
                  Weight {sortKey === 'position_pct' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th className="numeric" onClick={() => handleSort('pct_to_stop')} style={{ cursor: 'pointer' }}>
                  % to Stop {sortKey === 'pct_to_stop' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th className="numeric">Stop $</th>
                <th>Signal</th>
                {onJournalClick && <th style={{ width: 36 }}></th>}
              </tr>
            </thead>
            <tbody>
              {sortedPositions.map((position) => {
                const pnlPct = ((position.current_price - position.avg_cost) / position.avg_cost) * 100
                const pnlClass = position.unrealized_pnl >= 0 ? 'positive' : 'negative'
                const pctToStop = computePctToStop(position)
                const stopColor = pctToStop == null ? 'var(--text-muted)'
                  : pctToStop < 3  ? 'var(--negative)'
                  : pctToStop < 7  ? 'var(--warning)'
                  : 'var(--text-secondary)'
                const stopWeight = pctToStop != null && pctToStop < 7 ? 700 : undefined

                return (
                  <tr key={position.id}>
                    <td className="ticker">{position.ticker}</td>
                    <td>
                      <span className={`sleeve-badge ${position.sleeve}`}>
                        {position.sleeve}
                      </span>
                    </td>
                    <td className="numeric">{position.quantity}</td>
                    <td className="numeric">{formatCurrency(position.avg_cost)}</td>
                    <td className="numeric">{formatCurrency(position.current_price)}</td>
                    <td className={`numeric pnl ${pnlClass}`}>
                      {formatCurrency(position.unrealized_pnl)}
                      <br />
                      <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                        {formatPercent(pnlPct)}
                      </span>
                    </td>
                    <td className="numeric">{formatCurrency(position.market_value)}</td>
                    <td className="numeric">{position.position_pct?.toFixed(1)}%</td>
                    <td className="numeric" style={{ color: stopColor, fontWeight: stopWeight }}>
                      {pctToStop != null ? `${pctToStop.toFixed(1)}%` : '—'}
                    </td>
                    <td className="numeric" style={{ color: 'var(--text-muted)' }}>
                      {position.stop_price ? formatCurrency(position.stop_price) : '—'}
                    </td>
                    <td>
                      {position.entry_signal && (
                        <span className="card-badge" style={{
                          background: position.entry_signal.includes('NewHi')
                            ? 'var(--positive-bg)'
                            : 'var(--accent-cyan-glow)',
                          color: position.entry_signal.includes('NewHi')
                            ? 'var(--positive)'
                            : 'var(--accent-cyan)',
                          fontSize: '0.65rem',
                        }}>
                          {position.entry_signal}
                        </span>
                      )}
                    </td>
                    {onJournalClick && (
                      <td>
                        <button
                          className="journal-icon-btn"
                          title="Add journal entry"
                          onClick={(e) => { e.stopPropagation(); onJournalClick(position.ticker) }}
                        >
                          ✎
                        </button>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border-medium)' }}>
                <td colSpan={6} style={{ fontWeight: 600 }}>
                  TOTAL
                </td>
                <td className="numeric" style={{ fontWeight: 600 }}>
                  {formatCurrency(sleeveTotals.total || 0)}
                </td>
                <td className="numeric" style={{ fontWeight: 600 }}>
                  100%
                </td>
                <td colSpan={onJournalClick ? 4 : 3}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
